import type { IntegrityReport } from "../../domain/report/integrity-report-domain.js";
import type { ChecklistComponent, ChecklistPhoto } from "../../domain/inspection/checklist-domain.js";
import type { Recommendation } from "../../domain/recommendation/recommendation-domain.js";
import type { DocumentSnapshot } from "../../infrastructure/documents/document-engine.js";
import { formatReportValue, NOT_INFORMED } from "../../shared/format/report-value.js";
import type { GeneratedLineList } from "../../domain/inspection-book/line-list.js";

export interface UnifiedReportMapperInput {
  report: IntegrityReport;
  inspection?: any;
  project?: any;
  equipmentSnapshot?: any;
  safetyDevices?: any[];
  instruments?: any[];
  checklist?: any;
  checklistComponents?: readonly ChecklistComponent[] | any[];
  performedInspectionMethods?: any[];
  recommendations?: readonly Recommendation[] | any[];
  photos?: readonly ChecklistPhoto[] | any[];
  refDocs?: Array<{ name: string; description?: string; type?: string }> | any[];
  signatories?: {
    inspector?: { name: string; cred?: string; digitalSignature?: any } | null;
    plh?: { name: string; cred?: string; digitalSignature?: any } | null;
    fiscal?: { name: string; cred?: string } | null;
  } | any[];
  client?: { name: string; id?: string } | null;
  /**
   * LINHAS QUE COMPOE O SISTEMA (comando complementar): presente só quando o
   * Equipamento do RI é um SISTEMA DE TUBULAÇÃO — fonte única
   * (PipingSystemLineListService), a mesma que alimenta o Book e o PDF
   * independente. `null`/ausente em qualquer outro contexto de Equipamento.
   */
  generatedLineList?: GeneratedLineList | null;
  /**
   * STUDIO INSPECT IA: logo documental da versão visual que vale para ESTE RI
   * (carimbada na aprovação; V0 = texto INSPECT IA). null = texto de sempre.
   */
  tenantLogo?: string | null;
}

export interface IntegrityReportDocumentModel {
  safetyDevices: any[];
  instruments: any[];
  clientName: string;
  report: {
    id: string;
    reportNumber: string;
    clientReportNumber: string | null;
    revision: string;
    status: string;
    tenantLogo?: string | null;
    verificationToken?: string | null;
    verificationCode?: string | null;
    documentHash?: string | null;
    issueDate?: string | null;
    objective: string;
    referenceDocuments: string;
    referenceDocsList: Array<{ name: string; description: string; type: string }>;
    inspectionObservations: string;
    maintenanceServices: string;
    nextInspectionDates: string;
    damageMechanisms: string;
    conclusion: string;
    operationalCondition: string;
    modality: string;
    osNumber: string;
    subOsNumber: string;
    inspectorName: string;
    inspectorCrt: string;
    engineerPlhName: string;
    engineerPlhCrea: string;
    fiscalName: string;
    restrictions: string;
    condition: string;
    residualLifeAdopted?: string;
    aptPeriodDays?: string;
    residualLifeSource?: string;
  };
  equipment: {
    tag: string;
    name: string;
    equipmentType: string;
    equipmentTypeDisplay: string;
    unit: string;
    area: string;
    location: string;
    manufacturer: string;
    manufacturingYear: string;
    serialNumber: string;
    internalCode: string;
    nr13Category: string;
    fluidClass: string;
    service: string;
    material: string;
    projectCode: string;
    fabricationStandard: string;
    fabricationCode: string;
    dimensionalStandard: string;
    projectDrawingFlowchart: string;
    designPressure: string;
    designTemperature: string;
    operatingPressure: string;
    operatingTemperature: string;
    pmta: string;
    pth: string;
    nominalThickness: string;
    corrosionAllowance: string;
    jointEfficiency: string;
    diameter: string;
    volume: string;
    tmin: string;
    tact: string;
    cr: string;
    remainingLife: string;
    fromLocation?: string;
    toLocation?: string;
    internalCoating?: string;
    externalCoating?: string;
    properties: Record<string, any>;
  };
  inspection: {
    id: string;
    startDate: string;
    endDate: string;
    modality: string;
    nature: string;
    inspectionType: string;
    operationalCondition: string;
    osNumber: string;
    subOsNumber: string;
  };
  checklistComponents: Array<{
    id: string;
    groupId: string;
    definitionComponentId: string;
    result: string;
    resultDisplay: string;
    resultColor: string;
    description: string;
    inspectionNotes: string;
    recommendationCode: string;
    evidence: string;
    hasPhotos: boolean;
  }>;
  performedMethods: Array<{
    id: string;
    method: string;
    methodName: string;
    reportNumber: string;
    date: string;
    result: string;
    resultDisplay: string;
    notes: string;
  }>;
  recommendations: Array<{
    id: string;
    code: string;
    criticality: string;
    criticalityDisplay: string;
    discipline: string;
    executeIn: string;
    executeInDisplay: string;
    scope: string;
    zr: string;
    componentId?: string | null;
    checklistItemId?: string | null;
  }>;
  photos: Array<{
    id: string;
    image: string;
    component: string;
    comment: string;
  }>;
  signatories: Array<{
    name: string;
    role: string;
    roleDisplay: string;
    cred: string;
    signatureStatus: string;
    signatureImage?: string | null;
    isBlankSignature: boolean;
  }>;
  /** LINHAS QUE COMPOE O SISTEMA — `null` fora do contexto Sistema de Tubulação. */
  generatedLineList: GeneratedLineList | null;
}

export class UnifiedInspectionIntegrityReportMapper {

  /** Delegação ao formatter documental central — a regra "NÃO INFORMADO" é uma só em todo o sistema. */
  private static formatMeasurement(val: any, fallback = NOT_INFORMED): string {
    return formatReportValue(val, { fallback });
  }

  private static mapEquipmentType(type: string | undefined | null): { raw: string; display: string } {
    if (!type) return { raw: 'NÃO INFORMADO', display: 'NÃO INFORMADO' };
    const clean = String(type).trim().toUpperCase();
    switch (clean) {
      case 'PRESSURE_VESSEL':
        return { raw: 'PRESSURE_VESSEL', display: 'Vaso de Pressão' };
      case 'STORAGE_TANK':
        return { raw: 'STORAGE_TANK', display: 'Tanque de Armazenamento' };
      case 'HEAT_EXCHANGER':
        return { raw: 'HEAT_EXCHANGER', display: 'Trocador de Calor' };
      case 'BOILER':
        return { raw: 'BOILER', display: 'Caldeira' };
      case 'FURNACE':
        return { raw: 'FURNACE', display: 'Forno Industrial' };
      case 'PIPING_SYSTEM':
        return { raw: 'PIPING_SYSTEM', display: 'Sistema de Tubulação' };
      case 'PIPING_LINE':
        return { raw: 'PIPING_LINE', display: 'Linha de Tubulação' };
      case 'OTHER':
        return { raw: 'OTHER', display: 'Outro Equipamento' };
      default:
        return { raw: type, display: type };
    }
  }

  private static mapCriticality(crit: string | undefined | null): { raw: string; display: string } {
    const clean = String(crit || 'MODERATE').toUpperCase();
    switch (clean) {
      case 'CRITICAL':
        return { raw: 'CRITICAL', display: 'CRÍTICA' };
      case 'HIGH':
        return { raw: 'HIGH', display: 'ALTA' };
      case 'MODERATE':
      case 'MEDIUM':
        return { raw: 'MODERATE', display: 'MÉDIA' };
      case 'LOW':
        return { raw: 'LOW', display: 'BAIXA' };
      default:
        return { raw: clean, display: clean };
    }
  }

  private static mapExecution(exec: string | undefined | null): { raw: string; display: string } {
    const clean = String(exec || 'ROUTINE').toUpperCase();
    switch (clean) {
      case 'SHUTDOWN':
      case 'PARADA':
        return { raw: 'SHUTDOWN', display: 'Parada' };
      case 'ROUTINE':
      case 'ROTINA':
      default:
        return { raw: 'ROUTINE', display: 'Rotina' };
    }
  }


  private static mapModality(modality: string | undefined | null): string {
    const clean = String(modality || '').trim().toUpperCase();
    switch (clean) {
      case 'INTERNAL': return 'INTERNA';
      case 'EXTERNAL': return 'EXTERNA';
      case 'GENERAL': return 'GERAL';
      default: return clean || 'NÃO INFORMADO';
    }
  }

  private static mapNature(nature: string | undefined | null): string {
    const clean = String(nature || '').trim().toUpperCase();
    switch (clean) {
      case 'INITIAL': return 'INICIAL';
      case 'PERIODIC': return 'PERIÓDICA';
      case 'EXTRAORDINARY': return 'EXTRAORDINÁRIA';
      default: return clean || 'NÃO INFORMADO';
    }
  }

  private static mapStatus(status: string | undefined | null): string {
    const clean = String(status || '').trim().toUpperCase();
    switch (clean) {
      case 'DRAFT':
      case 'IN_ELABORATION': return 'EM ELABORAÇÃO';
      case 'AWAITING_SIGNATURE': return 'AGUARDANDO ASSINATURAS';
      case 'SIGNED':
      case 'COMPLETED': return 'CONCLUÍDO';
      case 'CANCELED': return 'CANCELADO';
      default: return clean || 'EM ELABORAÇÃO';
    }
  }

  private static mapFluidClass(fluidClass: string | undefined | null): string {
    const clean = String(fluidClass || '').trim().toUpperCase();
    if (clean === 'CLASSE_A') return 'CLASSE A';
    if (clean === 'CLASSE_B') return 'CLASSE B';
    if (clean === 'CLASSE_C') return 'CLASSE C';
    if (clean === 'CLASSE_D') return 'CLASSE D';
    return fluidClass || 'NÃO INFORMADO';
  }

  public static mapToDocumentModel(input: UnifiedReportMapperInput): IntegrityReportDocumentModel {
    const rep = input.report;
    const insp = input.inspection || {};

    // Equipment snapshot handling: support equipmentData wrapper or direct snapshot
    const snap = (input.equipmentSnapshot || {}) as any;
    const eqRaw = snap.equipmentData || snap;
    const props = { ...(eqRaw.properties || {}), ...eqRaw };

    const eqTypeMapping = this.mapEquipmentType(eqRaw.equipmentType || props.equipmentType);

    const clientName = input.client?.name || snap.clientName || eqRaw.clientName || (rep as any).clientName || 'NÃO INFORMADO';
    const resolvedProjectName = input.project?.name || input.inspection?.projectName || (input as any).projectName || snap.projectName || eqRaw.projectName;
    const projectName = (resolvedProjectName && resolvedProjectName !== 'NÃO INFORMADO') ? resolvedProjectName : 'NÃO INFORMADO';
    const adminProjectCode = input.project?.code || input.inspection?.projectCode || snap.projectCode || '';
    const technicalDesignCode = eqRaw.designCode || eqRaw.projectCode || props.designCode || props.projectCode || '';
    const unitAreaName = snap.unitAreaName || eqRaw.unitAreaName || eqRaw.area || props.area || 'NÃO INFORMADO';
    const unitAreaCode = snap.unitAreaCode || eqRaw.unitAreaCode || '';

    // Format all technical equipment attributes without hardcoded values
    const equipmentMapped = {
      tag: eqRaw.tag || props.tag || 'NÃO INFORMADO',
      name: eqRaw.name || props.name || eqRaw.service || props.service || 'NÃO INFORMADO',
      clientName,
      projectName,
      projectCode: technicalDesignCode || 'NÃO INFORMADO',
      adminProjectCode: adminProjectCode || 'NÃO INFORMADO',
      unitAreaName,
      unitAreaCode,
      equipmentType: eqTypeMapping.raw,
      equipmentTypeDisplay: eqTypeMapping.display,
      fromLocation: eqRaw.fromReference || eqRaw.fromEquipmentId || props.fromReference || '-',
      toLocation: eqRaw.toReference || eqRaw.toEquipmentId || props.toReference || '-',
      unit: eqRaw.unit || props.unit || unitAreaName,
      area: unitAreaName,
      location: eqRaw.location || props.location || unitAreaName,
      manufacturer: eqRaw.manufacturer || props.manufacturer || 'NÃO INFORMADO',
      manufacturingYear: this.formatMeasurement(eqRaw.manufacturingYear || props.manufacturingYear),
      serialNumber: eqRaw.serialNumber || props.serialNumber || 'NÃO INFORMADO',
      internalCode: eqRaw.internalCode || props.internalCode || 'NÃO INFORMADO',
      nr13Category: eqRaw.nr13Category || props.nr13Category || 'NÃO INFORMADO',
      fluidClass: this.mapFluidClass(eqRaw.fluidClass || props.fluidClass),
      service: eqRaw.service || props.service || props.fluid || 'NÃO INFORMADO',
      material: eqRaw.material || props.material || 'NÃO INFORMADO',
      fabricationStandard: eqRaw.fabricationStandard || props.fabricationStandard || props.manufacturingCode || eqRaw.constructionCode || props.constructionCode || props.designConstructionCode || props.norma || 'NÃO INFORMADO',
      dimensionalStandard: eqRaw.dimensionalStandard || props.dimensionalStandard || 'NÃO INFORMADO',
      projectDrawingFlowchart: eqRaw.projectDrawingFlowchart || props.projectDrawingFlowchart || (() => {
        try {
          if (eqRaw.payloadJson) {
            const p = typeof eqRaw.payloadJson === 'string' ? JSON.parse(eqRaw.payloadJson) : eqRaw.payloadJson;
            return p?.project?.projectDrawingFlowchart || p?.design?.projectDrawingFlowchart || null;
          }
        } catch { }
        return null;
      })() || 'NÃO INFORMADO',
      designPressure: this.formatMeasurement(props.designPressure),
      designTemperature: this.formatMeasurement(props.designTemperature),
      operatingPressure: this.formatMeasurement(props.operatingPressure),
      operatingTemperature: this.formatMeasurement(props.operatingTemperature),
      pmta: eqRaw.pmtaValue ? this.formatMeasurement({ value: eqRaw.pmtaValue, unit: eqRaw.pmtaUnit || 'kgf/cm²' }) : this.formatMeasurement(props.pmta || props.mawp),
      pth: this.formatMeasurement(props.pth || props.testPressure || props.hydrostaticTestPressure),
      nominalThickness: this.formatMeasurement(props.nominalThickness),
      corrosionAllowance: this.formatMeasurement(props.corrosionAllowance),
      jointEfficiency: this.formatMeasurement(props.jointEfficiency),
      diameter: this.formatMeasurement(props.diameter),
      nps: props.nps || 'NÃO INFORMADO',
      schedule: props.schedule ? `Sch ${props.schedule}${props.pipingDesignation ? ` (${props.pipingDesignation})` : ''}` : (props.pipingDesignation || 'NÃO INFORMADO'),
      outsideDiameter: this.formatMeasurement(props.outsideDiameter),
      calculatedInnerDiameter: this.formatMeasurement(props.calculatedInnerDiameter),
      volume: this.formatMeasurement(props.volume),
      tmin: this.formatMeasurement(props.tmin),
      tact: this.formatMeasurement(props.tact || props.tAct || props.tactMeasured),
      cr: this.formatMeasurement(props.cr || props.corrosionRate),
      remainingLife: this.formatMeasurement(props.remainingLife || props.vidaRemanescente, 'NÃO AVALIADO'),
      system: eqRaw.pipeSystemApplicability === 'NOT_APPLICABLE' ? 'NÃO APLICÁVEL' : (eqRaw.system || props.system || 'NÃO INFORMADO'),
      internalCoating: eqRaw.internalCoating || props.internalCoating || (eqRaw.payloadJson && JSON.parse(eqRaw.payloadJson).design?.internalCoating) || '—',
      externalCoating: eqRaw.externalCoating || props.externalCoating || (eqRaw.payloadJson && JSON.parse(eqRaw.payloadJson).design?.externalCoating) || '—',
      spec: props.spec || eqRaw.spec || 'NÃO INFORMADO',
      pressureClass: props.pressureClass || eqRaw.pressureClass || 'NÃO INFORMADO',
      fabricationCode: props.fabricationCode || eqRaw.fabricationCode || 'NÃO INFORMADO',
      properties: Object.keys(props).length > 0 ? props : (eqRaw as any)
    };

    // Inspection Metadata
    const inspectionMapped = {
      id: insp.id || rep.inspectionId || 'NÃO INFORMADO',
      startDate: insp.startDate ? new Date(insp.startDate).toLocaleDateString('pt-BR') : 'NÃO INFORMADO',
      endDate: insp.endDate ? new Date(insp.endDate).toLocaleDateString('pt-BR') : (insp.startDate ? new Date(insp.startDate).toLocaleDateString('pt-BR') : 'NÃO INFORMADO'),
      modality: this.mapModality(insp.modality),
      nature: this.mapNature(insp.nature),
      inspectionType: insp.inspectionType || 'Integridade NR-13',
      operationalCondition: insp.operationalCondition || 'Operando',
      osNumber: insp.osNumber || (rep as any).osNumber || 'NÃO INFORMADO',
      subOsNumber: insp.subOsNumber || (rep as any).subOsNumber || 'NÃO INFORMADO'
    };

    // Reference Documents: map from explicit list or parse from string
    const refDocsList: Array<{ name: string; description: string; type: string }> = [];
    const sourceRefDocs = Array.isArray(input.refDocs) && input.refDocs.length > 0
      ? input.refDocs
      : (Array.isArray((rep as any).refDocs) ? (rep as any).refDocs : []);

    if (sourceRefDocs.length > 0) {
      for (const doc of sourceRefDocs) {
        refDocsList.push({
          name: doc.name || doc.code || 'NÃO INFORMADO',
          description: doc.description || doc.title || '-',
          type: doc.type || 'Norma / Documento de Referência'
        });
      }
    } else if (rep.referenceDocuments && rep.referenceDocuments.trim().length > 0) {
      const items = rep.referenceDocuments.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean);
      for (const item of items) {
        refDocsList.push({
          name: item,
          description: 'Documento Técnico Aplicável',
          type: 'Referência Normativa'
        });
      }
    }

    // Deduplicate and map photos
    const rawPhotos = input.photos || [];
    const photoMap = new Map<string, any>();
    rawPhotos.forEach((p, idx) => {
      const imgKey = p.id || p.fileUrl || p.image || p.url || `photo-${idx}`;
      if (!photoMap.has(imgKey)) {
        const foundComp = input.checklistComponents?.find(c => c.id === p.componentId);
        const compName = p.component || p.title || foundComp?.name || foundComp?.definitionComponentId || foundComp?.groupId || 'Componente';
        photoMap.set(imgKey, {
          id: p.id || `photo-${idx + 1}`,
          image: p.fileUrl || p.image || p.url || '',
          component: compName,
          comment: p.comment || p.description || 'Registro fotográfico da inspeção.',
          componentId: p.componentId || p.checklistItemId || null,
          componentName: compName
        });
      }
    });

    // Also include photos embedded inside checklist components with their specific comments
    if (input.checklistComponents && Array.isArray(input.checklistComponents)) {
      input.checklistComponents.forEach((c: any, cIdx: number) => {
        const compName = c.name || c.definitionComponentId || (c.groupId && c.groupId !== 'default' ? c.groupId : '') || `Componente ${cIdx + 1}`;
        if (c.photos && Array.isArray(c.photos)) {
          c.photos.forEach((ph: any, phIdx: number) => {
            const imgUrl = typeof ph === 'string' ? ph : (ph.url || ph.fileUrl || ph.image || '');
            const phComment = typeof ph === 'string' ? '' : (ph.comment || '');
            const imgKey = typeof ph === 'string' ? ph : (ph.id || `${c.id || cIdx}-photo-${phIdx}`);
            if (imgUrl && !photoMap.has(imgKey)) {
              photoMap.set(imgKey, {
                id: `photo-${c.id || cIdx}-${phIdx + 1}`,
                image: imgUrl,
                component: compName,
                comment: phComment || 'Registro fotográfico da inspeção.',
                componentId: c.id || null,
                componentName: compName
              });
            }
          });
        }
      });
    }

    const photosMapped = Array.from(photoMap.values());

    // Recommendations mapping
    const rawRecs = input.recommendations || [];
    const recommendationsMapped: any[] = rawRecs.map((r, idx) => {
      const crit = this.mapCriticality(r.criticality);
      const exec = this.mapExecution(r.executeIn);
      const code = r.recommendationNumber || r.code || `REC-${(idx + 1).toString().padStart(3, '0')}`;
      return {
        id: code,
        code,
        criticality: crit.raw,
        criticalityDisplay: crit.display,
        discipline: r.discipline || 'MECÂNICA',
        executeIn: exec.raw,
        executeInDisplay: exec.display,
        scope: r.scope || r.description || 'NÃO INFORMADO',
        zr: (r.zr && r.zr !== 'undefined') ? r.zr : '-',
        componentId: r.componentId || null,
        checklistItemId: r.checklistItemId || null,
        componentName: r.componentName || r.component || null
      };
    });

    // Merge any embedded recommendations from checklistComponents without duplicating
    const rawComps = input.checklistComponents || [];
    rawComps.forEach((c) => {
      const compName = c.name || (c.groupId && c.groupId !== 'default' ? c.groupId : c.definitionComponentId) || c.id || 'Componente';
      if (c.recommendations && Array.isArray(c.recommendations) && c.recommendations.length > 0) {
        c.recommendations.forEach((rec: any, rIdx: number) => {
          const recCode = rec.code || rec.id || `REC-${(recommendationsMapped.length + 1).toString().padStart(3, '0')}`;
          const recDesc = rec.description || rec.scope || '';
          const alreadyExists = recommendationsMapped.some(rm =>
            (rm.code && rm.code === recCode) ||
            (rm.id && rm.id === recCode) ||
            (rm.scope && recDesc && rm.scope.includes(recDesc))
          );
          if (!alreadyExists && recDesc.trim()) {
            const crit = this.mapCriticality(rec.criticality);
            const exec = this.mapExecution(rec.execution || rec.executeIn);
            const fullScope = recDesc.startsWith(compName) ? recDesc : `${compName}: ${recDesc}`;
            recommendationsMapped.push({
              id: recCode,
              code: recCode,
              criticality: crit.raw,
              criticalityDisplay: crit.display,
              discipline: rec.discipline || 'CALDEIRARIA',
              executeIn: exec.raw,
              executeInDisplay: exec.display,
              scope: fullScope,
              zr: (rec.zr && rec.zr !== 'undefined') ? rec.zr : '-',
              componentId: c.id || null,
              checklistItemId: c.id || null,
              componentName: compName
            });
          }
        });
      }
    });

    // Checklist Components mapping
    const checklistMapped = rawComps.map((c) => {
      const result = c.result || c.status || 'PENDING';
      let resultDisplay = 'PENDÊNCIA DE PREENCHIMENTO';
      let resultColor = '#D97706';

      if (result === 'APPROVED') {
        resultDisplay = 'APROVADO';
        resultColor = '#15803D';
      } else if (result === 'REJECTED') {
        resultDisplay = 'REPROVADO';
        resultColor = '#DC2626';
      } else if (result === 'NOT_APPLICABLE') {
        resultDisplay = 'NÃO APLICÁVEL';
        resultColor = '#64748B';
      } else if (result === 'CLIENT_DISCRETION') {
        resultDisplay = 'A CRITÉRIO DO CLIENTE';
        resultColor = '#0369a1';
      }

      const compName = c.name || c.definitionComponentId || (c.groupId && c.groupId !== 'default' ? c.groupId : '') || c.id || '';
      const normCompName = (compName || '').toLowerCase().trim();
      const normCName = (c.name || '').toLowerCase().trim();
      const normCGroup = (c.groupId || '').toLowerCase().trim();
      const normCDef = (c.definitionComponentId || '').toLowerCase().trim();
      const cIdStr = (c.id || '').toLowerCase();

      // Check for real matching recommendations linking to this exact component
      const matchingRecs = recommendationsMapped.filter(r => {
        // 1. Direct ID matches
        if (r.checklistItemId && (r.checklistItemId === c.id || r.checklistItemId === c.definitionComponentId || r.checklistItemId === c.groupId)) return true;
        if (r.componentId && (r.componentId === c.id || r.componentId === c.definitionComponentId || r.componentId === c.groupId)) return true;
        if (cIdStr && r.componentId && (cIdStr.includes(r.componentId.toLowerCase()) || r.componentId.toLowerCase().includes(cIdStr))) return true;
        if (cIdStr && r.checklistItemId && (cIdStr.includes(r.checklistItemId.toLowerCase()) || r.checklistItemId.toLowerCase().includes(cIdStr))) return true;

        // 2. Component Name matches (normalized)
        const rName = (r.componentName || r.component || '').toLowerCase().trim();
        if (rName && (rName === normCompName || rName === normCName || rName === normCGroup || rName === normCDef)) return true;

        // 3. Embedded recommendation code or ID matches
        if (c.recommendations && Array.isArray(c.recommendations)) {
          if (c.recommendations.some((cr: any) =>
            (cr.code && cr.code === r.code) ||
            (cr.id && cr.id === r.id) ||
            (cr.description && r.scope && (r.scope.includes(cr.description) || cr.description.includes(r.scope)))
          )) return true;
        }

        // 4. Scope prefix match: e.g. "Costado: realizar ensaio..." or "Costado -"
        const rScope = (r.scope || '').toLowerCase().trim();
        if (normCompName && (rScope.startsWith(normCompName + ':') || rScope.startsWith(normCompName + ' -') || rScope.startsWith(normCompName + ' –'))) return true;
        if (normCName && (rScope.startsWith(normCName + ':') || rScope.startsWith(normCName + ' -') || rScope.startsWith(normCName + ' –'))) return true;
        if (normCGroup && normCGroup !== 'default' && (rScope.startsWith(normCGroup + ':') || rScope.startsWith(normCGroup + ' -'))) return true;

        return false;
      });

      // Populate recommendation code for Item 08
      let recommendationCode = '-';
      if (matchingRecs.length > 0) {
        const uniqueCodes = Array.from(new Set(matchingRecs.map(r => r.code).filter(Boolean)));
        recommendationCode = uniqueCodes.length > 0 ? uniqueCodes.join(', ') : '-';
      } else if (c.recommendations && Array.isArray(c.recommendations) && c.recommendations.length > 0) {
        const uniqueCodes = Array.from(new Set(c.recommendations.map((r: any, rIdx: number) => r.code || `REC-${(rIdx + 1).toString().padStart(3, '0')}`).filter(Boolean)));
        recommendationCode = uniqueCodes.length > 0 ? uniqueCodes.join(', ') : '-';
      }

      // Check evidence: find all matching photo indices linked to this component
      const matchingPhotoNumbers: string[] = [];
      photosMapped.forEach((p, pIdx) => {
        const pNum = (pIdx + 1).toString().padStart(2, '0');
        const isMatch = (p.componentId && (p.componentId === c.id || p.componentId === c.definitionComponentId)) ||
          (p.component && (p.component === compName || p.component === c.name || p.component === c.groupId || p.component === c.definitionComponentId)) ||
          (c.photos && Array.isArray(c.photos) && c.photos.some((ph: any) => {
            const phUrl = typeof ph === 'string' ? ph : (ph.url || ph.fileUrl || ph.image || '');
            const phId = typeof ph === 'string' ? ph : ph.id;
            return (phUrl && phUrl === p.image) || (phId && phId === p.id);
          }));
        if (isMatch) {
          matchingPhotoNumbers.push(`Foto ${pNum}`);
        }
      });

      const evidence = matchingPhotoNumbers.length > 0 ? matchingPhotoNumbers.join(', ') : '-';
      const hasPhotos = matchingPhotoNumbers.length > 0;

      // Technical description fallback
      let desc = c.description || c.condition;
      if (!desc || desc.trim() === '') {
        if (result === 'APPROVED') desc = 'Sem anomalias visíveis';
        else if (result === 'REJECTED') desc = 'Descrição técnica da não conformidade não informada';
        else if (result === 'NOT_APPLICABLE') desc = 'Não aplicável ao escopo da inspeção';
        else if (result === 'CLIENT_DISCRETION') desc = 'A critério do cliente';
        else desc = 'Pendente de inspeção';
      }

      return {
        id: c.id,
        groupId: c.groupId || compName,
        definitionComponentId: c.definitionComponentId || c.name || compName || c.id,
        result,
        resultDisplay,
        resultColor,
        description: desc,
        inspectionNotes: c.inspectionNotes || c.technicalComment || '-',
        recommendationCode,
        evidence,
        hasPhotos
      };
    });

    // Performed Methods / Tests (NDTs / Inspections)
    const rawMethods = input.performedInspectionMethods || (rep as any).ndts || [];
    const performedMethodsMapped = rawMethods.map((m: any, idx: number) => {
      const res = m.result || m.res || 'APPROVED';
      const isApproved = res === 'APPROVED' || res === 'Conforme' || res === 'Aprovado';
      const isRejected = res === 'REJECTED' || res === 'Reprovado' || res === 'Não Conforme';
      const resultDisplay = isApproved ? 'APROVADO' : (isRejected ? 'REPROVADO' : (res || 'CONCLUÍDO'));

      const rawCode = m.methodId || m.method || m.methodName || m.name || '';
      const methodCode = String(rawCode).trim().toUpperCase();
      let methodName = '';

      if (methodCode === 'VISUAL' || methodCode === 'INSPEÇÃO VISUAL' || methodCode === 'INSPECAO VISUAL' || methodCode === 'EV') {
        methodName = 'INSPEÇÃO VISUAL';
      } else if (methodCode === 'THICKNESS' || methodCode === 'ME' || methodCode === 'MEDIÇÃO DE ESPESSURA' || methodCode === 'MEDICAO DE ESPESSURA' || methodCode === 'MEDIÇÃO DE ESPESSURA (ME)' || methodCode === 'MEDICAO DE ESPESSURA (ME)') {
        methodName = 'MEDIÇÃO DE ESPESSURA (ME)';
      } else if (methodCode === 'REMOTE_VISUAL' || methodCode === 'RVI' || methodCode === 'INSPEÇÃO VISUAL REMOTA' || methodCode === 'INSPECAO VISUAL REMOTA' || methodCode === 'INSPEÇÃO VISUAL REMOTA (RVI)' || methodCode === 'INSPECAO VISUAL REMOTA (RVI)') {
        methodName = 'INSPEÇÃO VISUAL REMOTA';
      } else if (methodCode === 'LIQUID_PENETRANT' || methodCode === 'LP' || methodCode === 'LÍQUIDO PENETRANTE' || methodCode === 'LIQUIDO PENETRANTE' || methodCode === 'LÍQUIDO PENETRANTE (LP)' || methodCode === 'LIQUIDO PENETRANTE (LP)') {
        methodName = 'LÍQUIDO PENETRANTE (LP)';
      } else if (methodCode === 'MAGNETIC_PARTICLES' || methodCode === 'PM' || methodCode === 'PARTÍCULAS MAGNÉTICAS' || methodCode === 'PARTICULAS MAGNETICAS' || methodCode === 'PARTÍCULAS MAGNÉTICAS (PM)' || methodCode === 'PARTICULAS MAGNETICAS (PM)') {
        methodName = 'PARTÍCULAS MAGNÉTICAS (PM)';
      } else if (methodCode === 'ULTRASOUND' || methodCode === 'US' || methodCode === 'USC' || methodCode === 'ULTRASSOM' || methodCode === 'ULTRASSOM CONVENCIONAL' || methodCode === 'ULTRASSOM CONVENCIONAL (US)') {
        methodName = 'ULTRASSOM CONVENCIONAL (US)';
      } else if (methodCode === 'BSCAN' || methodCode === 'B-SCAN' || methodCode === 'BSCAN / CSCAN' || methodCode === 'B-SCAN / C-SCAN') {
        methodName = 'B-SCAN / C-SCAN';
      } else if (methodCode === 'OTHER' || methodCode === 'OUTROS' || methodCode === 'OUTROS ENSAIOS') {
        methodName = m.otherName || m.notes || 'OUTROS ENSAIOS';
      } else if (methodCode) {
        methodName = m.name || m.methodName || `MÉTODO NÃO IDENTIFICADO: ${methodCode}`;
        console.warn(`[MAPPER-NDT] Método não identificado: ${methodCode}`);
      } else {
        methodName = 'MÉTODO NÃO IDENTIFICADO';
        console.warn(`[MAPPER-NDT] Item de ensaio sem identificação de método.`);
      }

      // Resolving report number and result for NDTs
      const isVisualMethod = methodCode === 'VISUAL' || methodCode === 'REMOTE_VISUAL' || methodCode === 'INSPEÇÃO VISUAL' || methodCode === 'INSPECAO VISUAL' || methodCode === 'INSPEÇÃO VISUAL REMOTA';
      let reportNumber = m.reportNumber || m.rel || '-';
      let finalResultDisplay = resultDisplay;

      if (!isVisualMethod && (!reportNumber || reportNumber === '-' || reportNumber === '')) {
        reportNumber = 'NÃO VINCULADO';
        if (isApproved) {
          finalResultDisplay = 'NÃO VINCULADO';
        }
      }

      return {
        id: m.id || `method-${idx + 1}`,
        method: methodCode || 'ME',
        methodName: methodName,
        reportNumber,
        date: m.date || m.data || '-',
        result: res,
        resultDisplay: finalResultDisplay,
        notes: m.notes || m.obs || m.comment || '-'
      };
    });

    // Signatories mapping: strict, no fictitious names
    const signatoriesMapped: Array<{
      name: string;
      role: string;
      roleDisplay: string;
      cred: string;
      signatureStatus: string;
      signatureImage?: string | null;
      isBlankSignature: boolean;
    }> = [];

    // Parse signatories input (can be array or structured object)
    let inspectorSig: any = null;
    let plhSig: any = null;
    let fiscalSig: any = null;

    if (Array.isArray(input.signatories)) {
      inspectorSig = input.signatories.find((s: any) => s.role === 'INSPECTOR');
      plhSig = input.signatories.find((s: any) => s.role === 'PLH' || s.role === 'ENGINEER');
      fiscalSig = input.signatories.find((s: any) => s.role === 'FISCAL' || s.role === 'FISCALIZAÇÃO');
    } else if (input.signatories && typeof input.signatories === 'object') {
      inspectorSig = input.signatories.inspector;
      plhSig = input.signatories.plh;
      fiscalSig = input.signatories.fiscal;
    }

    // 1. INSPETOR
    const inspectorName = inspectorSig?.name || (rep as any).inspectorName || (insp as any)?.inspectorName || (rep.inspectorId ? (rep.inspectorId.startsWith('PROF-') ? `Inspetor [${rep.inspectorId}]` : rep.inspectorId) : 'NÃO DEFINIDO');
    const inspectorCred = inspectorSig?.cred || (rep as any).inspectorCrt || (rep as any).inspectorCred || 'NÃO INFORMADO';
    signatoriesMapped.push({
      name: inspectorName,
      role: 'INSPECTOR',
      roleDisplay: 'INSPETOR DE EQUIPAMENTOS',
      cred: inspectorCred,
      signatureStatus: rep.inspectorId ? 'Assinado Digitalmente' : 'Pendente',
      signatureImage: inspectorSig?.signatureImage || inspectorSig?.signatureUrl || null,
      isBlankSignature: false
    });

    // 2. ENGENHEIRO PLH
    const plhName = plhSig?.name || (rep as any).engineerPlhName || (rep.engineerPlhId ? `Eng. PLH [${rep.engineerPlhId}]` : 'NÃO DEFINIDO');
    const plhCred = plhSig?.cred || (rep as any).engineerPlhCrea || (rep as any).plhCred || 'NÃO INFORMADO';
    signatoriesMapped.push({
      name: plhName,
      role: 'PLH',
      roleDisplay: 'RESPONSÁVEL TÉCNICO (PLH)',
      cred: plhCred,
      signatureStatus: rep.engineerPlhId && rep.status === 'SIGNED' ? 'Assinado Digitalmente' : 'Pendente',
      signatureImage: plhSig?.signatureImage || plhSig?.signatureUrl || null,
      isBlankSignature: false
    });

    // 3. FISCALIZAÇÃO - Mandatory blank signature for client physical signature/stamp
    signatoriesMapped.push({
      name: fiscalSig?.name || 'FISCALIZAÇÃO',
      role: 'FISCAL',
      roleDisplay: 'FISCALIZAÇÃO / CLIENTE',
      cred: 'FISCAL DO CONTRATO',
      signatureStatus: 'Assinatura Física / Carimbo',
      signatureImage: null,
      isBlankSignature: true
    });

    // Final Report document metadata
    const rawCond = rep.engineeringAssessment?.equipmentCondition || (rep as any).condition;
    let mappedCondition = 'EM AVALIAÇÃO';
    if (rawCond) {
      const cleanCond = String(rawCond).toUpperCase().trim();
      if (cleanCond === 'APTO' || cleanCond === 'APPROVED') {
        mappedCondition = 'APTO';
      } else if (cleanCond === 'NAO_APTO' || cleanCond === 'NÃO APTO' || cleanCond === 'REJECTED' || cleanCond === 'REPROVADO') {
        mappedCondition = 'NÃO APTO';
      } else if (cleanCond === 'CRITERIO_CLIENTE' || cleanCond === 'CRITÉRIO DO CLIENTE' || cleanCond === 'A CRITÉRIO DO CLIENTE') {
        mappedCondition = 'A CRITÉRIO DO CLIENTE';
      } else {
        mappedCondition = rawCond;
      }
    }

    const mappedConclusion = rep.conclusion && String(rep.conclusion).trim().length > 0 && rep.conclusion !== 'NÃO INFORMADO'
      ? String(rep.conclusion).trim()
      : 'NÃO INFORMADO';

    const rawRestr = (rep as any).restrictions;
    const mappedRestrictions = rawRestr && String(rawRestr).trim().length > 0 && rawRestr !== 'NÃO INFORMADO' && rawRestr !== '-'
      ? String(rawRestr).trim()
      : 'Nenhuma restrição imposta à operação regular do equipamento.';

    // Resolve Residual Life and Aptitude Period for Section 14 (Conclusão)
    const resLife = rep.engineeringAssessment?.residualLife;
    let mappedResidualLife = 'NÃO INFORMADA';
    let mappedAptPeriod = 'NÃO INFORMADO';
    let mappedResidualLifeSource = '-';

    if (resLife && typeof resLife.adoptedMonths === 'number' && Number.isFinite(resLife.adoptedMonths) && resLife.adoptedMonths > 0) {
      const yearsVal = (resLife.adoptedMonths / 12).toFixed(1).replace('.', ',');
      mappedResidualLife = `${yearsVal} anos (${resLife.adoptedMonths} meses)`;
      const totalDays = Math.round(resLife.adoptedMonths * 30.4375);
      mappedAptPeriod = `${totalDays.toLocaleString('pt-BR')} dias`;
      if (resLife.source?.avrNumber) {
        mappedResidualLifeSource = `${resLife.source.avrNumber} / Rev. ${resLife.source.revision || '00'}`;
      } else if (resLife.origin === 'ENGINEER') {
        mappedResidualLifeSource = 'INFORMADA PELO ENGENHEIRO';
      }
    }

    const reportMapped = {
      id: rep.id,
      reportNumber: rep.reportNumber || rep.id,
      clientReportNumber: rep.clientReportNumber || null,
      revision: rep.revision || (rep as any).version?.toString().padStart(2, '0') || '00',
      status: this.mapStatus(rep.status),
      tenantLogo: input.tenantLogo || null,
      verificationToken: rep.verificationToken || null,
      verificationCode: rep.verificationCode || null,
      documentHash: rep.documentHash || null,
      issueDate: rep.updatedAt || rep.createdAt || new Date().toISOString(),
      objective: rep.objective && rep.objective !== 'NÃO INFORMADO' ? rep.objective : 'NÃO INFORMADO',
      referenceDocuments: rep.referenceDocuments || (refDocsList.length > 0 ? refDocsList.map(d => d.name).join(', ') : 'NÃO INFORMADO'),
      referenceDocsList: refDocsList,
      inspectionObservations: rep.inspectionObservations || 'NÃO INFORMADO',
      maintenanceServices: rep.maintenanceServices || 'NÃO INFORMADO',
      nextInspectionDates: rep.nextInspectionDates || 'A definir pelo Profissional Habilitado (PLH)',
      damageMechanisms: rep.damageMechanisms || 'Não foram identificados mecanismos de dano ativos nesta inspeção.',
      conclusion: mappedConclusion,
      operationalCondition: insp.operationalCondition || 'Operando',
      modality: this.mapModality(insp.modality),
      osNumber: inspectionMapped.osNumber,
      subOsNumber: inspectionMapped.subOsNumber,
      inspectorName,
      inspectorCrt: inspectorCred,
      engineerPlhName: plhName,
      engineerPlhCrea: plhCred,
      fiscalName: 'FISCALIZAÇÃO',
      restrictions: mappedRestrictions,
      condition: mappedCondition,
      residualLifeAdopted: mappedResidualLife,
      aptPeriodDays: mappedAptPeriod,
      residualLifeSource: mappedResidualLifeSource
    };

    return {
      safetyDevices: input.safetyDevices || [],
      instruments: input.instruments || [],
      clientName,
      report: reportMapped,
      equipment: equipmentMapped,
      inspection: inspectionMapped,
      checklistComponents: checklistMapped,
      performedMethods: performedMethodsMapped,
      recommendations: recommendationsMapped,
      photos: photosMapped,
      signatories: signatoriesMapped,
      generatedLineList: input.generatedLineList ?? null
    };
  }

  public static mapToDocumentSnapshot(input: UnifiedReportMapperInput): DocumentSnapshot {
    const model = this.mapToDocumentModel(input);

    return {
      id: input.report.id,
      type: 'RI',
      revision: model.report.revision,
      createdAt: new Date(input.report.createdAt || Date.now()),
      documentNumber: model.report.reportNumber,
      // O documento identifica o próprio Cliente — a geração não depende do
      // cliente global da sessão.
      clientId: input.report.clientId,
      data: model
    };
  }

  public static validateConsistency(input: UnifiedReportMapperInput): {
    valid: boolean;
    errors: string[];
    warnings: string[];
  } {
    const errors: string[] = [];
    const warnings: string[] = [];

    const rep = input.report;
    if (!rep) {
      errors.push('O relatório de integridade é obrigatório.');
      return { valid: false, errors, warnings };
    }

    // Equipment validation
    const eq = input.equipmentSnapshot?.equipmentData || input.equipmentSnapshot;
    if (!eq) {
      errors.push('Snapshot do equipamento não encontrado para esta inspeção.');
    } else {
      if (!eq.tag || eq.tag.trim() === '') {
        errors.push('Equipamento não possui TAG cadastrado.');
      }
    }

    // Inspection link validation
    if (!rep.inspectionId) {
      errors.push('Relatório de integridade não está vinculado a uma Inspeção.');
    }

    // Checklist validation
    const comps = input.checklistComponents || [];
    const pendingComps = comps.filter(c => c.result === 'PENDING' || !c.result);

    // Check for "default" or unresolved components
    for (const c of comps) {
      const name = String((c.groupId && c.groupId !== 'default' ? c.groupId : c.definitionComponentId) || '').trim().toLowerCase();
      if (!name || name === 'default' || name === '') {
        errors.push("COMPONENTE DO CHECKLIST SEM VÍNCULO VÁLIDO: O componente não pôde ser identificado.");
      }
    }

    if (pendingComps.length > 0) {
      warnings.push(`Existem ${pendingComps.length} componente(s) com pendência de preenchimento no checklist.`);
    }

    const rejectedComps = comps.filter(c => c.result === 'REJECTED');
    for (const rej of rejectedComps) {
      if (!rej.description && !rej.condition) {
        errors.push(`Componente REPROVADO "${rej.groupId || rej.definitionComponentId || rej.id}" não possui descrição do defeito/anomalia.`);
      }
    }

    // Recommendations validation: verify each recommendation has required fields
    const recs = input.recommendations || [];
    for (const r of recs) {
      if (!r.recommendationNumber) {
        errors.push(`Recomendação sem número gerado (UUID interno exibido: ${r.id})`);
      }
    }
    for (const r of recs) {
      if (!r.discipline) {
        warnings.push(`Recomendação ${r.id} sem disciplina definida.`);
      }
      if (!r.scope) {
        errors.push(`Recomendação ${r.id} sem escopo/ação técnica definida.`);
      }
    }

    // Signatories validation
    if (rep.status === 'AWAITING_SIGNATURE' || rep.status === 'SIGNED' || rep.status === 'COMPLETED') {
      if (!rep.inspectorId) {
        errors.push('Inspetor responsável obrigatório para assinatura e conclusão.');
      }
      if (!rep.engineerPlhId) {
        errors.push('Engenheiro PLH obrigatório para assinatura e conclusão.');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }
}
